import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useInView } from "framer-motion";
import {
  ArrowRight, Quote, Shield, Award, Lightbulb, Heart,
  Code2, Server, Lock, MapPin, CheckCircle2, Star
} from "lucide-react";
import { useTranslation } from "@/i18n/translations";
import SEO from "@/components/SEO";

function A({ children, className = "", delay = 0 }) {
  const ref = useRef(null);
  const v = useInView(ref, { once: true, margin: "-60px" });
  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 28 }}
      animate={v ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.7, delay, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
}

const HERO_IMG = "https://images.unsplash.com/photo-1543007630-9710e4a00a20?auto=format&fit=crop&w=1600&q=70";
const FOUNDER_IMG = "/founder-emrah.jpg";
const STORY_IMG = "https://images.unsplash.com/photo-1414235077428-338989a2e8c0?auto=format&fit=crop&w=1200&q=70";
const SWISS_IMG = "https://images.unsplash.com/photo-1530841377377-3ff06c0ca713?auto=format&fit=crop&w=1600&q=70";

export default function AboutPage() {
  const { t: tr } = useTranslation();
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const VALUES = [
    { icon: Shield, title: tr("about.v1Title"), desc: tr("about.v1Desc") },
    { icon: Award, title: tr("about.v2Title"), desc: tr("about.v2Desc") },
    { icon: Lightbulb, title: tr("about.v3Title"), desc: tr("about.v3Desc") },
    { icon: Heart, title: tr("about.v4Title"), desc: tr("about.v4Desc") },
  ];

  const TECH = [
    { icon: Code2, title: tr("about.tech1"), desc: tr("about.tech1D") },
    { icon: Server, title: tr("about.tech2"), desc: tr("about.tech2D") },
    { icon: Lock, title: tr("about.tech3"), desc: tr("about.tech3D") },
    { icon: MapPin, title: tr("about.tech4"), desc: tr("about.tech4D") },
  ];

  const CERTS = [
    "PCI-DSS Niveau 1",
    "FINMA",
    "Swiss QR-bill",
    "CAMT.054",
    "RGPD / LPD",
    "ISO 27001",
    "3D Secure 2.0",
    "EMV Level 1 & 2",
  ];

  return (
    <div data-testid="about-page">
      <SEO page="about" />

      {/* HERO — editorial, large typography, Swiss authority */}
      <section className="relative pt-32 pb-20 lg:pt-40 lg:pb-32 bg-[#0A1128] overflow-hidden">
        <div className="absolute inset-0 opacity-25">
          <img src={HERO_IMG} alt="" loading="lazy" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1128]/70 via-[#0A1128]/85 to-[#0A1128]"></div>
        </div>
        <div className="relative max-w-6xl mx-auto px-6 md:px-12">
          <A>
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00C853]">
              {tr("about.tag")}
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-white tracking-tight mt-5 leading-[1.05] max-w-5xl">
              {tr("about.title1")}{" "}
              <span className="text-[#00C853]">{tr("about.title2")}</span>{" "}
              {tr("about.title3")}
            </h1>
            <p className="text-base md:text-xl text-slate-300 mt-8 max-w-2xl leading-relaxed">
              {tr("about.intro")}
            </p>
          </A>
        </div>
      </section>

      {/* STORY — Editorial 2-column with story image */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
            <A className="lg:col-span-5">
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00C853]">
                {tr("about.storyTag")}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">
                {tr("about.storyTitle")}
              </h2>
              <div className="mt-8 rounded-2xl overflow-hidden border border-slate-200">
                <img src={STORY_IMG} alt="Restaurant familial" loading="lazy" className="w-full h-72 lg:h-96 object-cover" />
              </div>
            </A>
            <A className="lg:col-span-7" delay={0.15}>
              <div className="space-y-6 text-base md:text-lg text-[#475569] leading-relaxed">
                <p className="text-xl md:text-2xl text-[#0A1128] font-medium leading-relaxed">
                  {tr("about.storyLead")}
                </p>
                <p>{tr("about.storyP1")}</p>
                <p>{tr("about.storyP2")}</p>
                <p>{tr("about.storyP3")}</p>
              </div>
              <div className="mt-10 grid grid-cols-2 gap-6 pt-10 border-t border-slate-100">
                <div>
                  <div className="text-3xl font-bold text-[#1B2B5B]">2024</div>
                  <div className="text-sm text-[#475569] mt-1">{tr("about.founded")}</div>
                </div>
                <div>
                  <div className="text-3xl font-bold text-[#1B2B5B]">100%</div>
                  <div className="text-sm text-[#475569] mt-1">{tr("about.swissBuilt")}</div>
                </div>
              </div>
            </A>
          </div>
        </div>
      </section>

      {/* FOUNDER — Big editorial quote with portrait */}
      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
            <A className="lg:col-span-5 lg:order-2">
              <div className="relative">
                <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-2xl shadow-slate-200/60">
                  <img src={FOUNDER_IMG} alt={tr("about.founderName")} loading="lazy" className="w-full h-[420px] object-cover" />
                </div>
                <div className="absolute -bottom-6 -left-6 bg-white rounded-2xl border border-slate-200 p-5 shadow-xl max-w-xs">
                  <div className="text-sm font-semibold text-[#0A1128]">{tr("about.founderName")}</div>
                  <div className="text-xs text-[#475569] mt-0.5">{tr("about.founderRole")}</div>
                  <div className="flex items-center gap-1 mt-2.5 text-[#00C853]">
                    {[1,2,3,4,5].map(i => <Star key={i} size={11} fill="currentColor" />)}
                  </div>
                </div>
              </div>
            </A>
            <A className="lg:col-span-7 lg:order-1" delay={0.15}>
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00C853]">
                {tr("about.founderTag")}
              </span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">
                {tr("about.founderTitle")}
              </h2>
              <div className="relative mt-10 pl-8">
                <Quote className="absolute -left-2 -top-2 text-[#00C853]/20" size={56} strokeWidth={1.5} />
                <blockquote className="text-lg md:text-2xl text-[#0A1128] leading-relaxed font-medium italic">
                  {tr("about.founderQuote")}
                </blockquote>
              </div>
              <p className="mt-8 text-base md:text-lg text-[#475569] leading-relaxed">
                {tr("about.founderBio")}
              </p>
            </A>
          </div>
        </div>
      </section>

      {/* MISSION — Single bold statement */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <A>
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00C853]">
              {tr("about.missionTag")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A1128] tracking-tight mt-5 leading-[1.15]">
              {tr("about.missionTitle")}
            </h2>
            <p className="text-base md:text-xl text-[#475569] mt-8 max-w-3xl mx-auto leading-relaxed">
              {tr("about.missionDesc")}
            </p>
          </A>
        </div>
      </section>

      {/* VALUES — 4 cards grid */}
      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <A className="mb-16 max-w-2xl">
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00C853]">
              {tr("about.valuesTag")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">
              {tr("about.valuesTitle")}
            </h2>
          </A>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {VALUES.map((v, i) => (
              <A key={i} delay={i * 0.08}>
                <div className="bg-white rounded-2xl border border-slate-100 p-8 lg:p-10 h-full feature-card">
                  <div className="flex items-start gap-5">
                    <div className="w-12 h-12 rounded-xl bg-[#00C853]/10 flex items-center justify-center shrink-0">
                      <v.icon size={22} className="text-[#00C853]" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-[#0A1128] mb-3">{v.title}</h3>
                      <p className="text-sm md:text-base text-[#475569] leading-relaxed">{v.desc}</p>
                    </div>
                  </div>
                </div>
              </A>
            ))}
          </div>
        </div>
      </section>

      {/* TECH STACK — Swiss-built + Adyen partnership */}
      <section className="py-24 lg:py-32 bg-[#0A1128] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img src={SWISS_IMG} alt="" loading="lazy" className="w-full h-full object-cover" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12">
          <A className="max-w-2xl mb-16">
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00C853]">
              {tr("about.techTag")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-white tracking-tight mt-4 leading-[1.1]">
              {tr("about.techTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-300 mt-6 leading-relaxed">
              {tr("about.techDesc")}
            </p>
          </A>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {TECH.map((t, i) => (
              <A key={i} delay={i * 0.08}>
                <div className="bg-white/5 backdrop-blur-sm rounded-xl border border-white/10 p-6 h-full">
                  <div className="w-10 h-10 rounded-lg bg-[#00C853]/15 flex items-center justify-center mb-5">
                    <t.icon size={20} className="text-[#00C853]" />
                  </div>
                  <h3 className="text-base font-semibold text-white mb-2">{t.title}</h3>
                  <p className="text-sm text-slate-400 leading-relaxed">{t.desc}</p>
                </div>
              </A>
            ))}
          </div>

          {/* Adyen partner spotlight */}
          <A delay={0.2}>
            <div className="mt-16 bg-gradient-to-br from-white/[0.06] to-white/[0.02] rounded-2xl border border-white/10 p-8 lg:p-12">
              <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <span className="inline-block text-[10px] font-bold tracking-[0.25em] uppercase text-[#00C853] mb-3">
                    {tr("about.partnerTag")}
                  </span>
                  <h3 className="text-2xl md:text-3xl font-semibold text-white tracking-tight leading-tight">
                    {tr("about.adyenTitle")}
                  </h3>
                  <p className="text-base text-slate-300 mt-5 leading-relaxed">
                    {tr("about.adyenDesc")}
                  </p>
                </div>
                <div className="lg:col-span-5 grid grid-cols-2 gap-3">
                  {[
                    { label: tr("about.adyenS1L"), value: tr("about.adyenS1") },
                    { label: tr("about.adyenS2L"), value: tr("about.adyenS2") },
                    { label: tr("about.adyenS3L"), value: tr("about.adyenS3") },
                    { label: tr("about.adyenS4L"), value: tr("about.adyenS4") },
                  ].map((s, i) => (
                    <div key={i} className="bg-white/5 rounded-xl p-4 border border-white/10">
                      <div className="text-2xl font-bold text-[#00C853]">{s.value}</div>
                      <div className="text-[11px] text-slate-400 mt-1 uppercase tracking-wider">{s.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </A>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="py-24 lg:py-32 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <A className="text-center mb-12">
            <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#00C853]">
              {tr("about.certTag")}
            </span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-semibold text-[#0A1128] tracking-tight mt-4 leading-[1.1]">
              {tr("about.certTitle")}
            </h2>
            <p className="text-base text-[#475569] mt-5 max-w-2xl mx-auto leading-relaxed">
              {tr("about.certDesc")}
            </p>
          </A>
          <A delay={0.15}>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {CERTS.map((cert, i) => (
                <div key={i} className="flex items-center gap-2.5 bg-[#F8F9FA] rounded-xl border border-slate-100 px-4 py-3.5">
                  <CheckCircle2 size={16} className="text-[#00C853] shrink-0" />
                  <span className="text-sm font-medium text-[#0A1128]">{cert}</span>
                </div>
              ))}
            </div>
          </A>
        </div>
      </section>

      {/* PARENT COMPANY — AyaPos SARL */}
      <section className="py-24 lg:py-32 bg-[#F8F9FA]">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <A className="bg-white rounded-3xl border border-slate-100 p-10 lg:p-16 shadow-sm">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-2 h-2 rounded-full bg-[#00C853]"></div>
              <span className="text-xs font-bold tracking-[0.25em] uppercase text-[#475569]">
                {tr("about.parentTag")}
              </span>
            </div>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-semibold text-[#0A1128] tracking-tight leading-tight">
              {tr("about.parentTitle")}
            </h2>
            <p className="text-base md:text-lg text-[#475569] mt-6 leading-relaxed">
              {tr("about.parentDesc")}
            </p>
            <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-6 pt-8 border-t border-slate-100">
              {[
                { v: tr("about.pa1V"), l: tr("about.pa1L") },
                { v: tr("about.pa2V"), l: tr("about.pa2L") },
                { v: tr("about.pa3V"), l: tr("about.pa3L") },
                { v: tr("about.pa4V"), l: tr("about.pa4L") },
              ].map((s, i) => (
                <div key={i}>
                  <div className="text-xl md:text-2xl font-bold text-[#1B2B5B]">{s.v}</div>
                  <div className="text-xs text-[#475569] mt-1">{s.l}</div>
                </div>
              ))}
            </div>
          </A>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-24 lg:py-32 bg-[#1B2B5B]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <A>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1]">
              {tr("about.ctaTitle")}
            </h2>
            <p className="text-base md:text-lg text-slate-300 mt-6 max-w-xl mx-auto leading-relaxed">
              {tr("about.ctaDesc")}
            </p>
            <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-3 mt-10">
              <Link
                to="/contact"
                data-testid="about-cta"
                className="inline-flex items-center justify-center gap-2 font-semibold bg-[#00C853] text-white rounded-full px-8 py-3.5 text-sm hover:bg-[#00A844] transition-all shadow-[0_4px_20px_rgba(0,200,83,0.3)]"
              >
                {tr("about.ctaBtn")} <ArrowRight size={16} />
              </Link>
              <Link
                to="/solutions"
                className="inline-flex items-center justify-center font-semibold border-2 border-white/20 text-white rounded-full px-8 py-3.5 text-sm hover:bg-white/10 transition-all"
              >
                {tr("about.ctaBtn2")}
              </Link>
            </div>
          </A>
        </div>
      </section>
    </div>
  );
}
